import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, ChevronUp, ChevronDown, AlertCircle, ShoppingCart, Clock } from 'lucide-react';
import { purchaseOrderAPI } from '@/lib/api';
import { toast } from 'sonner';

export default function OpenPurchaseOrders({ user }) {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Load cached data from database on page entry (fast)
  const loadCachedData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await purchaseOrderAPI.getAll();
      setPurchaseOrders(response.data.purchase_orders || []);
      setLastSync(response.data.last_sync);
    } catch (err) {
      console.error('Error loading cached purchase orders:', err);
      setError(err.response?.data?.detail || 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  // Sync from NetSuite (slow) - only on manual refresh
  const syncFromNetSuite = async () => {
    setSyncing(true);
    setError(null);
    try {
      toast.info('Syncing from NetSuite... This may take a minute.');
      const response = await purchaseOrderAPI.sync();
      setLastSync(response.data.last_sync);
      toast.success(`Synced ${response.data.count} purchase orders from NetSuite`);
      // Reload cached data after sync
      await loadCachedData();
    } catch (err) {
      console.error('Error syncing purchase orders:', err);
      setError(err.response?.data?.detail || 'Failed to sync from NetSuite');
      toast.error('Failed to sync from NetSuite');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadCachedData();
  }, []);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.key === key) {
        return {
          key,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedPurchaseOrders = useMemo(() => {
    if (!sortConfig.key) return purchaseOrders;

    return [...purchaseOrders].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [purchaseOrders, sortConfig]);

  const columns = [
    { key: 'work_order', label: 'Work Order' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'purchase_order', label: 'Purchase Order' },
    { key: 'customer_po', label: 'Customer PO' },
    { key: 'cust_sn', label: 'Cust SN' },
    { key: 'cust_tag', label: 'Cust Tag' },
    { key: 'project_name', label: 'Project Name' }
  ];

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUp className="h-4 w-4 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="h-4 w-4 text-sky-600" />
    ) : (
      <ChevronDown className="h-4 w-4 text-sky-600" />
    );
  };

  const formatLastSync = (isoDate) => {
    if (!isoDate) return 'Never synced';
    const date = new Date(isoDate);
    return date.toLocaleString();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Open Purchase Orders</h1>
          <p className="text-muted-foreground mt-1">
            Purchase orders from ICS (Subsidiary 4)
          </p>
        </div>
        <Button
          onClick={syncFromNetSuite}
          disabled={syncing}
          className="bg-sky-600 hover:bg-sky-700"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync from NetSuite'}
        </Button>
      </div>

      {/* Stats Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-sky-100 rounded-lg">
                <ShoppingCart className="h-6 w-6 text-sky-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Open POs</p>
                <p className="text-2xl font-bold">{purchaseOrders.length}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>Last sync: {formatLastSync(lastSync)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sync Info Banner */}
      {purchaseOrders.length === 0 && !loading && !error && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-amber-700">
              <AlertCircle className="h-5 w-5" />
              <span>No cached data. Click "Sync from NetSuite" to load purchase orders.</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">Purchase Orders</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-sky-600" />
              <span className="ml-3 text-muted-foreground">Loading...</span>
            </div>
          ) : purchaseOrders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No purchase orders in cache</p>
              <p className="text-sm mt-2">Click "Sync from NetSuite" to load data</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors select-none"
                        onClick={() => handleSort(column.key)}
                      >
                        <div className="flex items-center gap-1">
                          {column.label}
                          <SortIcon columnKey={column.key} />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedPurchaseOrders.map((po, index) => (
                    <tr
                      key={po.id}
                      className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                        index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                      }`}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-sky-600">
                        {po.work_order || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {po.vendor || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant="outline" className="font-mono">
                          {po.purchase_order || '-'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {po.customer_po || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 font-mono">
                        {po.cust_sn || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {po.cust_tag || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 max-w-xs truncate" title={po.project_name}>
                        {po.project_name || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
